import logging from 'utils/logging';
import { MOMENT_TYPES, HOST_STATUS, requestConversationalNarration, narrationReady, selectAllNarrations } from '../hostStateSlice';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_FINAL_REVEALING,
  FINAL_REVEAL_STEP_INTRO,
  FINAL_REVEAL_STEP_PLAYER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
  FINAL_REVEAL_STEP_WAGER_REACTION,
  FINAL_REVEAL_STEP_STANDINGS,
  FINAL_REVEAL_STEP_WINNER,
} from '../../gamestate/constants';
import { advanceState, advanceFinalRevealStep } from '../../gamestate/GameState';
import { updateGameStateAction } from '../../gamestate/stateActions';
import { registerCacheReset } from '../momentCacheManager';
import { selectAllPlayers, selectPlayerById } from '../../player/playerSlice';
import { selectAllSubmissions } from '../../question/submissionSlice';
import { selectGameObject } from '../../gamestate/gameStateSlice';
import { selectAllQuestions } from '../../question/questionSlice';
import { isSubmissionCorrect, isNoAnswer } from 'utils/submissionClassification';

const log = logging.getLogger('moments.FinalPlayerReveal');
log.setLevel('info');

let revealInitiated = false;

export const resetFinalPlayerRevealCache = () => {
  revealInitiated = false;
};
registerCacheReset('round', resetFinalPlayerRevealCache);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check we're in the final.revealing round state */
const inFinalRevealing = (gameState) =>
  gameState?.currentState === GAMESTATE_ROUND &&
  gameState?.round === GAMESTATE_ROUND_FINAL &&
  gameState?.roundState === GAMESTATE_ROUNDSTATE_FINAL_REVEALING;

/** Standard narration readiness check */
const isNarrationReady = (narration) =>
  narration?.textStatus === 'READY' &&
  (narration?.audioStatus === 'READY' || narration?.audioStatus === 'ERROR');

/**
 * Derive a narration key from momentType + optional playerId.
 * Per-player steps get suffixed: FINAL_PLAYER_INTRO_abc123
 * Global steps use the momentType directly: FINAL_REVEAL_INTRO
 */
const narrationKeyForStep = (momentType, playerId = null) =>
  playerId ? `${momentType}_${playerId}` : momentType;

/** Get current player data from Redux state for the active reveal player */
const getCurrentPlayerData = (state, currentPlayerId, questionId) => {
  const players = selectAllPlayers(state);
  const player = selectPlayerById(state, currentPlayerId);
  const submissions = selectAllSubmissions(state);
  const submission = submissions.find(
    s => s.playerId === currentPlayerId && s.questionId === questionId
  );
  return { player, submission, players, submissions };
};

/** Compute the current leader among already-revealed players */
const getLeaderAmongRevealed = (players, revealOrder, revealedUpTo) => {
  const revealed = new Set(revealOrder.slice(0, revealedUpTo));
  let leader = null;
  let leaderScore = -Infinity;
  for (const p of players) {
    const score = revealed.has(p.id) ? (p.finalScore ?? p.preFinalScore ?? 0) : (p.preFinalScore ?? 0);
    if (score > leaderScore) {
      leaderScore = score;
      leader = p;
    }
  }
  return { leader, leaderScore };
};

/**
 * Thunk: request a conversational narration and mark it ready in the orchestrator.
 * Derives the narration key from momentType + optional playerId.
 *
 * Pre-generation seam: if a narration with this key already exists in Redux
 * with text READY and audio READY/ERROR, skip the cloud call and just
 * dispatch narrationReady. Without this check, on-demand would fire a
 * duplicate LLM+TTS call for every moment the eager pre-gen pipeline in
 * functions/src/host/generateFinalRevealScript.js already covered.
 *
 * Usage: await dispatch(requestRevealNarration({ gameId, momentType, ... }));
 */
const requestRevealNarration = ({ gameId, momentType, context, conversationId, playerId = null }) =>
  async (dispatch, getState) => {
    const narrationKey = narrationKeyForStep(momentType, playerId);

    const existing = selectAllNarrations(getState()).find(n => n.key === narrationKey);
    if (existing && isNarrationReady(existing)) {
      log.info(`Reveal narration ${narrationKey} already present (pre-gen hit) — skipping cloud call`);
      dispatch(narrationReady({ gameId, narrationKey }));
      return;
    }

    log.info(`Requesting reveal narration: ${narrationKey} (momentType: ${momentType})`);
    try {
      await dispatch(requestConversationalNarration({
        gameId, narrationKey, momentType, context, conversationId,
      }));
      await dispatch(narrationReady({ gameId, narrationKey }));
    } catch (error) {
      log.error(`Error requesting reveal narration ${narrationKey}:`, error);
    }
  };

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

/**
 * Fires once when entering final.revealing with currentStep === null.
 * Builds the reveal order, writes it to state, advances to the first step,
 * and requests the intro narration.
 */
export const FinalPlayerRevealTrigger = {
  id: 'FINAL_PLAYER_REVEAL',
  shouldTrigger: ({ currentState, currentPlayers, submissions }) => {
    if (revealInitiated) return false;
    if (!inFinalRevealing(currentState)) return false;

    // Only fire when currentStep is null (freshly initialized)
    if (currentState?.finalReveal?.currentStep !== null) return false;
    if (!currentPlayers || currentPlayers.length === 0) return false;

    revealInitiated = true;
    log.info('Final player reveal sequence triggered');

    return {
      shouldTriggerAction: true,
      shouldGenerateNarration: false,
      triggerAction: async (gameObject, players, dispatch) => {
        await initiateRevealSequence(gameObject, players, dispatch);
      },
    };
  },
};

async function initiateRevealSequence(gameObject, players, dispatch) {
  const gameId = gameObject.id;
  const finalReveal = gameObject.state?.finalReveal || {};
  const { conversationId } = finalReveal;

  // Build reveal order: sorted by preFinalScore ascending (lowest first)
  const sortedPlayers = [...players].sort(
    (a, b) => (a.preFinalScore || 0) - (b.preFinalScore || 0)
  );
  const revealOrder = sortedPlayers.map(p => p.id);

  if (revealOrder.length === 0) {
    log.error('No players to reveal');
    return;
  }

  // Write revealOrder + advance to first step (final-reveal-intro)
  const newState = {
    ...gameObject.state,
    finalReveal: {
      ...finalReveal,
      revealOrder,
      currentStep: FINAL_REVEAL_STEP_INTRO,
      currentPlayerId: null,
    },
  };
  dispatch(updateGameStateAction(gameId, newState));

  await dispatch(requestNarrationForCurrentStep(gameId));
}

// ---------------------------------------------------------------------------
// Playback moment factory
// ---------------------------------------------------------------------------

/**
 * Creates a standard playback moment for a final reveal step.
 *
 * @param {string} id - Unique playback moment ID
 * @param {string} momentType - MOMENT_TYPES value
 * @param {string} gateStep - FINAL_REVEAL_STEP_* this moment plays during
 * @param {Function} getNarrationKey - (gameState) => narrationKey to match
 * @param {Function} onCompleteFn - (gameId, dispatch, getState) => void
 */
const createRevealPlayback = (id, momentType, gateStep, getNarrationKey, onCompleteFn) => ({
  id,
  momentType,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== momentType) return false;
    if (playedNarrations.has(narration.key)) return false;
    if (!inFinalRevealing(gameState)) return false;
    if (gameState?.finalReveal?.currentStep !== gateStep) return false;
    if (!isNarrationReady(narration)) return false;
    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    // For per-player steps, verify the narration key matches the current player
    const expectedKey = getNarrationKey(gameState);
    if (expectedKey && narration.key !== expectedKey) return false;

    return true;
  },

  onComplete: onCompleteFn,
});

// ---------------------------------------------------------------------------
// Per-step narration request — shared between onComplete handlers and the
// manual-advance FAB. Both arrive at "step entered" via different paths;
// this helper is the single source of truth for "ask for the narration of
// whatever step we're now on."
// ---------------------------------------------------------------------------

export const requestNarrationForCurrentStep = (gameId) => async (dispatch, getState) => {
  const state = getState();
  const gameObject = selectGameObject(state);
  if (!gameObject) return;

  const finalReveal = gameObject.state?.finalReveal || {};
  const {
    currentStep,
    currentPlayerId,
    revealOrder = [],
    correctAnswerAcknowledged,
    conversationId,
  } = finalReveal;
  const questionId = gameObject.state?.questionId;
  const players = selectAllPlayers(state);

  switch (currentStep) {
    case FINAL_REVEAL_STEP_INTRO: {
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_REVEAL_INTRO,
        context: { playerCount: players.length },
        conversationId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_PLAYER_INTRO: {
      const { player } = getCurrentPlayerData(state, currentPlayerId, questionId);
      const isFirst = revealOrder[0] === currentPlayerId;
      const isLast = revealOrder[revealOrder.length - 1] === currentPlayerId;
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_PLAYER_INTRO,
        context: {
          playerName: player?.name,
          preFinalScore: player?.preFinalScore || 0,
          isFirst,
          isLast,
        },
        conversationId,
        playerId: currentPlayerId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_ANSWER_INTRO: {
      const { player } = getCurrentPlayerData(state, currentPlayerId, questionId);
      const questions = selectAllQuestions(state);
      const question = questions.find(q => q.id === questionId);
      const correctAnswer = question?.answer || (question?.answers && question.answers[0]) || '';
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_ANSWER_INTRO,
        context: {
          playerName: player?.name,
          category: question?.category || 'Final Jeopardy',
          correctAnswer,
          correctAnswerAcknowledged,
        },
        conversationId,
        playerId: currentPlayerId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO: {
      const { player, submission, players: pp } = getCurrentPlayerData(state, currentPlayerId, questionId);
      const isCorrect = isSubmissionCorrect(submission);
      const noAnswer = isNoAnswer(submission);
      const isLastPlayer = revealOrder[revealOrder.length - 1] === currentPlayerId;
      const isFirstCorrect = isCorrect && !correctAnswerAcknowledged;
      const nobodyCorrectAndLast = isLastPlayer && !correctAnswerAcknowledged && !isCorrect;
      const revealCorrectAnswer = correctAnswerAcknowledged || isFirstCorrect || nobodyCorrectAndLast;
      const currentIndex = revealOrder.indexOf(currentPlayerId);
      const { leader, leaderScore } = getLeaderAmongRevealed(pp, revealOrder, currentIndex);
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_ANSWER_REACTION_WAGER_INTRO,
        context: {
          playerName: player?.name,
          noAnswer,
          isCorrect,
          isFirstCorrect,
          revealCorrectAnswer,
          isLastPlayer,
          preFinalScore: player?.preFinalScore || 0,
          currentLeaderScore: leader?.id !== currentPlayerId ? leaderScore : undefined,
        },
        conversationId,
        playerId: currentPlayerId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_WAGER_REACTION: {
      const { player, submission, players: pp } = getCurrentPlayerData(state, currentPlayerId, questionId);
      const correct = isSubmissionCorrect(submission);
      const wager = submission?.wager || 0;
      const preFinal = player?.preFinalScore || 0;
      const finalScore = player?.finalScore ?? (correct ? preFinal + wager : preFinal - wager);
      const currentIndex = revealOrder.indexOf(currentPlayerId);
      const { leader, leaderScore } = getLeaderAmongRevealed(pp, revealOrder, currentIndex);
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_WAGER_REACTION,
        context: {
          playerName: player?.name,
          wager,
          isCorrect: correct,
          preFinalScore: preFinal,
          finalScore,
          currentLeaderName: leader?.id !== currentPlayerId ? leader?.name : undefined,
          currentLeaderScore: leader?.id !== currentPlayerId ? leaderScore : undefined,
        },
        conversationId,
        playerId: currentPlayerId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_STANDINGS: {
      const { player, submission, players: pp } = getCurrentPlayerData(state, currentPlayerId, questionId);
      const currentIndex = revealOrder.indexOf(currentPlayerId);
      const hasMorePlayers = currentIndex < revealOrder.length - 1;
      const nextPlayerId = hasMorePlayers ? revealOrder[currentIndex + 1] : null;
      const nextPlayer = nextPlayerId ? pp.find(p => p.id === nextPlayerId) : null;
      const isCorrect = isSubmissionCorrect(submission);
      const wager = submission?.wager || 0;
      const preFinalScore = player?.preFinalScore || 0;
      const finalScore = player?.finalScore ?? (isCorrect ? preFinalScore + wager : preFinalScore - wager);
      const { leader, leaderScore } = getLeaderAmongRevealed(pp, revealOrder, currentIndex + 1);
      const scores = pp.map(p => {
        const revealed = revealOrder.slice(0, currentIndex + 1).includes(p.id);
        return revealed ? (p.finalScore ?? p.preFinalScore ?? 0) : (p.preFinalScore ?? 0);
      }).sort((a, b) => b - a);
      const currentPlacement = scores.indexOf(finalScore) + 1;
      const revealedSoFar = new Set(revealOrder.slice(0, currentIndex + 1));
      const unrevealedPlayers = pp.filter(p => !revealedSoFar.has(p.id));
      const remainingCount = unrevealedPlayers.length;
      const highestUnrevealedPreFinal = unrevealedPlayers.length > 0
        ? Math.max(...unrevealedPlayers.map(p => p.preFinalScore || 0))
        : 0;
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_STANDINGS,
        context: {
          playerName: player?.name,
          finalScore,
          currentPlacement,
          hasMorePlayers,
          nextPlayerName: nextPlayer?.name,
          nextPlayerPreFinalScore: nextPlayer?.preFinalScore || 0,
          leadingPlayerName: leader?.name,
          leadingScore: leaderScore,
          remainingCount,
          highestUnrevealedPreFinal,
        },
        conversationId,
        playerId: currentPlayerId,
      }));
      return;
    }

    case FINAL_REVEAL_STEP_WINNER: {
      const leaderboard = [...players]
        .map(p => ({ name: p.name, score: p.finalScore ?? p.preFinalScore ?? 0 }))
        .sort((a, b) => b.score - a.score);
      const winner = leaderboard[0];
      const isTie = leaderboard.length > 1 && leaderboard[0].score === leaderboard[1].score;
      const tiedPlayers = isTie ? leaderboard.filter(p => p.score === winner.score) : null;
      await dispatch(requestRevealNarration({
        gameId,
        momentType: MOMENT_TYPES.FINAL_WINNER,
        context: {
          winnerName: winner.name,
          winnerScore: winner.score,
          leaderboard,
          isTie,
          tiedPlayers,
        },
        conversationId,
      }));
      return;
    }

    default:
      log.warn(`requestNarrationForCurrentStep: unknown step "${currentStep}"`);
      return;
  }
};

// ---------------------------------------------------------------------------
// Per-step onComplete handlers
// ---------------------------------------------------------------------------

const onIntroComplete = async (gameId, dispatch, getState) => {
  log.info('Reveal intro complete, advancing to first player intro');
  const gameObject = selectGameObject(getState());
  if (!gameObject) return;
  const newReveal = await advanceFinalRevealStep(gameObject, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

const onPlayerIntroComplete = async (gameId, dispatch, getState) => {
  log.info('Player intro complete, advancing to answer intro');
  const gameObject = selectGameObject(getState());
  if (!gameObject) return;
  const newReveal = await advanceFinalRevealStep(gameObject, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

const onAnswerIntroComplete = async (gameId, dispatch, getState) => {
  log.info('Answer intro complete, advancing to answer-reaction-wager-intro');
  const gameObject = selectGameObject(getState());
  if (!gameObject) return;
  const newReveal = await advanceFinalRevealStep(gameObject, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

/**
 * Pre-advance: flip correctAnswerAcknowledged if this player was correct
 * or nobody got it and this was the last player. The flag affects spoiler
 * placement in the next narration's prompt.
 */
const onAnswerReactionWagerIntroComplete = async (gameId, dispatch, getState) => {
  log.info('Answer reaction + wager intro complete, advancing to wager reaction');
  const state = getState();
  let gameObj = selectGameObject(state);
  if (!gameObj) return;

  const finalReveal = gameObj.state?.finalReveal || {};
  const { currentPlayerId, revealOrder = [], correctAnswerAcknowledged } = finalReveal;
  const { submission } = getCurrentPlayerData(state, currentPlayerId, gameObj.state?.questionId);

  const isCorrect = isSubmissionCorrect(submission);
  const isLastPlayer = revealOrder[revealOrder.length - 1] === currentPlayerId;
  const nobodyCorrectAndLast = isLastPlayer && !correctAnswerAcknowledged && !isCorrect;

  if (!correctAnswerAcknowledged && (isCorrect || nobodyCorrectAndLast)) {
    const updatedState = {
      ...gameObj.state,
      finalReveal: {
        ...finalReveal,
        correctAnswerAcknowledged: true,
      },
    };
    dispatch(updateGameStateAction(gameId, updatedState));
    gameObj = { ...gameObj, state: updatedState };
  }

  const newReveal = await advanceFinalRevealStep(gameObj, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

/**
 * Pre-advance: commit this player's final score to Firestore. The standings
 * narration depends on persisted finalScore for placement math.
 */
const onWagerReactionComplete = async (gameId, dispatch, getState) => {
  log.info('Wager reaction complete, committing score and advancing to standings');
  const state = getState();
  const gameObject = selectGameObject(state);
  if (!gameObject) return;

  const { currentPlayerId } = gameObject.state?.finalReveal || {};
  const questionId = gameObject.state?.questionId;
  const allSubmissions = selectAllSubmissions(state);
  const playerSub = allSubmissions.find(
    s => s.playerId === currentPlayerId && s.questionId === questionId
  );
  if (playerSub) {
    try {
      const Provider = (await import('provider')).default;
      await Provider.commitFinalPlayerScore(gameId, currentPlayerId, playerSub.id, questionId);
    } catch (error) {
      log.error(`Error committing final score for ${currentPlayerId}:`, error);
    }
  }

  const newReveal = await advanceFinalRevealStep(gameObject, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

const onStandingsComplete = async (gameId, dispatch, getState) => {
  log.info('Standings complete, advancing to next player or winner');
  const gameObject = selectGameObject(getState());
  if (!gameObject) return;
  const newReveal = await advanceFinalRevealStep(gameObject, dispatch);
  if (!newReveal) return;
  await dispatch(requestNarrationForCurrentStep(gameId));
};

/** After winner narration: advance to credits */
const onWinnerComplete = async (gameId, dispatch, getState) => {
  log.info('Winner narration complete, advancing to credits');
  const state = getState();
  const gameObject = selectGameObject(state);
  if (!gameObject) return;
  const players = selectAllPlayers(state);
  await advanceState(gameObject, players, dispatch);
};

// ---------------------------------------------------------------------------
// Playback moments (one per step)
// ---------------------------------------------------------------------------

const RevealIntroPlayback = createRevealPlayback(
  'FINAL_REVEAL_INTRO_PLAYBACK',
  MOMENT_TYPES.FINAL_REVEAL_INTRO,
  FINAL_REVEAL_STEP_INTRO,
  () => narrationKeyForStep(MOMENT_TYPES.FINAL_REVEAL_INTRO),
  onIntroComplete
);

const PlayerIntroPlayback = createRevealPlayback(
  'FINAL_PLAYER_INTRO_PLAYBACK',
  MOMENT_TYPES.FINAL_PLAYER_INTRO,
  FINAL_REVEAL_STEP_PLAYER_INTRO,
  (gs) => narrationKeyForStep(MOMENT_TYPES.FINAL_PLAYER_INTRO, gs?.finalReveal?.currentPlayerId),
  onPlayerIntroComplete
);

const AnswerIntroPlayback = createRevealPlayback(
  'FINAL_ANSWER_INTRO_PLAYBACK',
  MOMENT_TYPES.FINAL_ANSWER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_INTRO,
  (gs) => narrationKeyForStep(MOMENT_TYPES.FINAL_ANSWER_INTRO, gs?.finalReveal?.currentPlayerId),
  onAnswerIntroComplete
);

const AnswerReactionWagerIntroPlayback = createRevealPlayback(
  'FINAL_ANSWER_REACTION_WAGER_INTRO_PLAYBACK',
  MOMENT_TYPES.FINAL_ANSWER_REACTION_WAGER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
  (gs) => narrationKeyForStep(MOMENT_TYPES.FINAL_ANSWER_REACTION_WAGER_INTRO, gs?.finalReveal?.currentPlayerId),
  onAnswerReactionWagerIntroComplete
);

const WagerReactionPlayback = createRevealPlayback(
  'FINAL_WAGER_REACTION_PLAYBACK',
  MOMENT_TYPES.FINAL_WAGER_REACTION,
  FINAL_REVEAL_STEP_WAGER_REACTION,
  (gs) => narrationKeyForStep(MOMENT_TYPES.FINAL_WAGER_REACTION, gs?.finalReveal?.currentPlayerId),
  onWagerReactionComplete
);

const StandingsPlayback = createRevealPlayback(
  'FINAL_STANDINGS_PLAYBACK',
  MOMENT_TYPES.FINAL_STANDINGS,
  FINAL_REVEAL_STEP_STANDINGS,
  (gs) => narrationKeyForStep(MOMENT_TYPES.FINAL_STANDINGS, gs?.finalReveal?.currentPlayerId),
  onStandingsComplete
);

const WinnerPlayback = createRevealPlayback(
  'FINAL_WINNER_PLAYBACK',
  MOMENT_TYPES.FINAL_WINNER,
  FINAL_REVEAL_STEP_WINNER,
  () => narrationKeyForStep(MOMENT_TYPES.FINAL_WINNER),
  onWinnerComplete
);

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

const api = {
  trigger: FinalPlayerRevealTrigger,
  playback: [
    RevealIntroPlayback,
    PlayerIntroPlayback,
    AnswerIntroPlayback,
    AnswerReactionWagerIntroPlayback,
    WagerReactionPlayback,
    StandingsPlayback,
    WinnerPlayback,
  ],
};

export default api;
